import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import {
  HomeworkStateFilter,
  homeworkState,
  latestBatchId,
  type StudentSummary,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { Empty } from "../../lib/Empty.tsx";
import { LoadingRows } from "../../lib/loading.tsx";
import { cleanupDrafts } from "./drafts.ts";
import { HubFilters } from "./hub/HubFilters.tsx";
import { HomeworkRow } from "./hub/HomeworkRow.tsx";
import { OverdueBlock } from "./hub/OverdueBlock.tsx";
import { TrashPanel } from "./hub/TrashPanel.tsx";
import { useHomeworkList } from "./hub/use-homework-list.ts";

/**
 * Хаб домашних работ (раздел 5 docs/homework-phase2-design.md).
 *
 * Требование владельца дословно: «я не хочу лезть в профиль ученика, чтобы
 * задать домашку или посмотреть его прошлые домашки, это должно быть всё на
 * экране домашки». Поэтому здесь есть всё сразу: кнопка выдачи, разрез по
 * ученику, фильтр состояния, переключатель последней выдачи, корзина и
 * долистывание. Карточка ученика остаётся коротким путём, но обязательной
 * остановкой быть перестаёт.
 *
 * У ученика это по-прежнему просто раздел «Домашка» кабинета: разрезов,
 * фильтров и корзины у него нет, потому что нечего разрезать.
 *
 * Состояния вычисляются из фактов (`homeworkState`), хранимого статуса нет.
 */

/** Разрез и фильтр живут в адресе: его пересылают и открывают в соседней вкладке. */
function stateOf(raw: string | null): HomeworkStateFilter {
  const parsed = HomeworkStateFilter.safeParse(raw);
  return parsed.success ? parsed.data : "all";
}

/** Работа, только что убранная в корзину с её карточки (5.10 фазы 2). */
function trashedIdOf(raw: unknown): string | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = (raw as { trashedId?: unknown }).trashedId;
  return typeof value === "string" ? value : null;
}

export function HomeworkListPage() {
  const { state: sessionState } = useSession();
  const [params, setParams] = useSearchParams();
  const location = useLocation();

  const studentUserId = params.get("studentId");
  const stateFilter = stateOf(params.get("state"));
  const trashedId = trashedIdOf(location.state);

  const teaching = sessionState.status === "signed" && sessionState.me.teaching === true;
  const userId = sessionState.status === "signed" ? sessionState.me.userId : null;

  const [students, setStudents] = useState<StudentSummary[] | null>(null);
  const [latestOnly, setLatestOnly] = useState(false);

  const list = useHomeworkList({ studentUserId, state: stateFilter });
  const rows = list.rows;

  // Список учеников нужен только разрезу, то есть только преподавателю
  useEffect(() => {
    if (!teaching) return;
    void (async () => {
      try {
        setStudents((await api.listStudents()).students);
      } catch {
        // Разрез без списка это выключенный селектор, а не пустой экран: сам
        // список работ уже приехал и полезен без него
        setStudents([]);
      }
    })();
  }, [teaching]);

  /**
   * Уборка черновиков (9.1 проекта): черновики домашек, которых больше нет, и
   * домашек, которым черновик больше нечего спасать.
   *
   * ⚠️ Правило «нет в списке, значит удаляем» действует ТОЛЬКО когда список
   * дочитан до конца и не сужен. С курсором первая страница это двадцать пять
   * работ, и уборка по ней стёрла бы черновики всех остальных.
   */
  useEffect(() => {
    if (userId === null || rows === null) return;
    const known = new Set(rows.map((h) => h.id));
    const finished = new Set(
      rows
        .filter(
          (h) =>
            h.revealedAt !== null &&
            homeworkState({ firstOpenedAt: h.firstOpenedAt, counters: h.counters }) === "done",
        )
        .map((h) => h.id),
    );
    cleanupDrafts(userId, {
      known,
      finished,
      complete: list.complete && studentUserId === null && stateFilter === "all",
    });
  }, [rows, userId, list.complete, studentUserId, stateFilter]);

  const latestBatch = useMemo(() => latestBatchId(rows ?? []), [rows]);
  const latestCount = useMemo(
    () => (rows ?? []).filter((h) => (h.batchId ?? null) === latestBatch && latestBatch !== null).length,
    [rows, latestBatch],
  );
  const shown = useMemo(
    () =>
      latestOnly && latestBatch !== null
        ? (rows ?? []).filter((h) => (h.batchId ?? null) === latestBatch)
        : (rows ?? []),
    [rows, latestOnly, latestBatch],
  );

  /** Адрес, в который карточка работы возвращается крошкой и после «В корзину». */
  const backTo = `/homework${params.toString() === "" ? "" : `?${params.toString()}`}`;
  /** Тот же разрез с фильтром «Просрочено»: ссылка из врезки над списком. */
  const overdueHref = (() => {
    const next = new URLSearchParams(params);
    next.set("state", "overdue");
    return `/homework?${next.toString()}`;
  })();
  const pickedName =
    students?.find((s) => s.userId === studentUserId)?.displayName ?? "этого ученика";
  const assignHref =
    studentUserId === null ? "/homework/new" : `/homework/new?studentId=${studentUserId}`;

  /*
    Разрез по одному ученику убирает колонку имени у ВСЕХ строк сразу и меняет
    шаблон колонок контейнера. Именно в этом была исходная беда списка: имя
    исчезало у той строки, у которой его не было, и колонки соседних строк
    разъезжались. Число колонок в шапке, в строках и в `--cols` обязано
    совпадать, поэтому решение принимается здесь, один раз на список.
  */
  const solo = studentUserId !== null;
  // Один момент на всю отрисовку: иначе соседние строки считали бы «сегодня» от
  // разных «сейчас», а на границе полуночи это разные ответы
  const now = Date.now();

  return (
    <main className="home home--list">
      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Домашка</h1>
          {/* Счётчика у заголовка нет намеренно: сколько работ в разрезе, уже
              сказано сегментом полосы фильтров («Все · 19»), а один факт двумя
              средствами на одном экране это то, ради чего заведено правило
              «факт не показывается дважды» */}
          {/*
            ⚠️ Главная кнопка стоит здесь ВСЕГДА, в том числе при пустом списке:
            она и есть ответ на требование «всё на экране домашки»
          */}
          {teaching && (
            <span className="head__act">
              <Link className="btn btn--primary" to={assignHref}>
                Задать домашку
              </Link>
            </span>
          )}
        </div>
      </header>

      {teaching && (
        <HubFilters
          students={students}
          studentUserId={studentUserId}
          onStudent={(id) => {
            const next = new URLSearchParams(params);
            if (id === null) next.delete("studentId");
            else next.set("studentId", id);
            setParams(next);
          }}
          state={stateFilter}
          onState={(value) => {
            const next = new URLSearchParams(params);
            if (value === "all") next.delete("state");
            else next.set("state", value);
            setParams(next);
          }}
          latestOnly={latestOnly}
          onLatestOnly={setLatestOnly}
          latestCount={latestCount}
          counts={list.counts}
          busy={list.busy}
        />
      )}

      {list.err !== null && (
        <p className="err">
          {list.err}{" "}
          <button className="btn btn--quiet" onClick={list.reload}>
            Повторить
          </button>
        </p>
      )}
      {/* Шаблон колонок тот же, что у настоящего списка ниже: у ученика колонки
          с именем преподавателя нет, и скелет обязан это знать, иначе приехавшая
          таблица переложит экран */}
      {rows === null && list.err === null && (
        <LoadingRows
          list={solo ? "list--hw-solo" : "list--hw"}
          cells={solo ? 4 : 5}
          narrow="page"
        />
      )}

      {rows !== null && rows.length === 0 && (
        <HomeworkEmpty
          teaching={teaching}
          students={students}
          studentName={studentUserId === null ? null : pickedName}
          filtered={stateFilter !== "all"}
          assignHref={assignHref}
        />
      )}

      {/*
        Просроченное видно СРАЗУ, без переключения фильтра: «не видно, что
        просрочено и у кого» это дословная жалоба владельца, а фильтр отвечает
        на неё только тому, кто догадался его нажать. При других фильтрах врезки
        нет: над списком просроченного она дубль, над выполненным противоречие
      */}
      {stateFilter === "all" && (
        <OverdueBlock
          studentUserId={studentUserId}
          hideCounterpart={solo}
          backTo={backTo}
          allHref={overdueHref}
          now={now}
        />
      )}

      {shown.length > 0 && (
        <ul className={solo ? "list list--hw-solo" : "list list--hw"} data-rows="page">
          <li className="list__head" aria-hidden="true">
            {!solo && <span>{teaching ? "Ученик" : "Преподаватель"}</span>}
            <span>Название</span>
            <span>Срок</span>
            <span>Ответы</span>
            <span>Состояние</span>
          </li>
          {shown.map((h) => (
            <HomeworkRow
              key={h.id}
              homework={h}
              hideCounterpart={solo}
              backTo={backTo}
              now={now}
            />
          ))}
        </ul>
      )}

      {/*
        Кнопка, а не бесконечная прокрутка: на канале 128 кбит/с подгрузка «сама»
        означает подгрузку в момент, когда человек просто проскроллил мимо
      */}
      {list.nextCursor !== null && (
        <p className="acts">
          <button className="btn" disabled={list.busy} onClick={list.loadMore}>
            {list.busy ? "Загрузка…" : "Показать ещё"}
          </button>
        </p>
      )}

      {teaching && (
        <TrashPanel
          studentUserId={studentUserId}
          highlightId={trashedId}
          onRestored={list.reload}
        />
      )}
    </main>
  );
}

/**
 * Пустые состояния (5.10 фазы 2).
 *
 * ⚠️ Текста «Задать домашнюю можно с карточки ученика» здесь больше нет: он и
 * был той самой отправкой в профиль ученика, которую требование владельца
 * отменяет.
 *
 * ⚠️ **С 14.09.2026 это одна плашка на пять случаев, а не пять абзацев**
 * (`lib/Empty.tsx`, подход 2з переноса дизайн-системы). Развилка осталась
 * прежней до строчки: разница между «нет ни одной работы», «нет по этому
 * фильтру» и «нет у этого ученика» это разные ответы на «что теперь», и
 * сводить их в один нельзя.
 *
 * ⚠️ Имя ученика едет в заголовок, а не в строку под ним: это ФАКТ о списке
 * («у Гриши работ пока нет»), а не объяснение к нему.
 */
function HomeworkEmpty(props: {
  teaching: boolean;
  students: StudentSummary[] | null;
  studentName: string | null;
  filtered: boolean;
  assignHref: string;
}) {
  if (props.filtered) {
    // Выход отсюда стоит тут же, в полосе фильтров над списком, и второй такой
    // же кнопкой в плашке мы бы показали пальцем на соседний контрол
    return <Empty icon="search" title="Ничего не нашлось" text="По этому фильтру домашек нет." />;
  }
  if (props.teaching && props.students !== null && props.students.length === 0) {
    // Форма выдачи без единого ученика бессмысленна: отправляем туда, где он заводится
    return (
      <Empty
        icon="students"
        title="Учеников пока нет"
        text="Домашку задают ученику, поэтому сначала заведите его и отправьте ему ссылку."
        action={{ label: "Завести ученика", to: "/students" }}
      />
    );
  }
  if (props.studentName !== null) {
    return (
      <Empty
        icon="homework"
        title={`У ${props.studentName} домашек пока нет`}
        action={{ label: "Задать домашку", to: props.assignHref }}
      />
    );
  }
  if (!props.teaching) {
    // Ученику предлагать нечего: домашнюю выдаёт преподаватель
    return (
      <Empty
        icon="homework"
        title="Домашек пока нет"
        text="Домашку задаёт преподаватель, и она появится здесь."
      />
    );
  }
  return (
    <Empty
      icon="homework"
      title="Домашек пока нет"
      action={{ label: "Задать домашку", to: props.assignHref }}
    />
  );
}
